import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Modal,
  StyleSheet,
  Platform,
  Image
} from "react-native";
// import DateTimePicker from "@react-native-community/datetimepicker";
import IdCard from "../../../../../../../../assets/images/id-card.png";
// import { Ionicons } from "@expo/vector-icons";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";

type Props = {
  onValidationChange?: (isValid: boolean) => void;
};

const BillPayDOBStep: React.FC = ({ onValidationChange }: Props) => {
  const { t } = useTranslation();
  const storedDobString = useSessionStore((s: SessionState) => (s as any).senderDOBString) || "";
  const [dob, setDob] = useState(storedDobString);
  const [showPicker, setShowPicker] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const setSenderDOBString = useSessionStore((s: SessionState) => (s as any).setSenderDOBString);
  const setSenderDOB = useSessionStore((s: SessionState) => (s as any).setSenderDOB);

  const isValid = dob.trim().length > 8;
  
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [isValid, onValidationChange]);
    
  // Keep local DOB in sync when store is hydrated (e.g., after AuthConsumer)
  useEffect(() => {
    setDob(storedDobString || "");
  }, [storedDobString]);

  // Mock ID verification function (replace with real API call)
  const verifyDOBWithID = async (enteredDob: string) => {
    console.log("Verifying DOB:", enteredDob);
    // Simulate backend check with previously uploaded ID
    return new Promise<boolean>((resolve) =>
      setTimeout(() => resolve(true), 1000) // always returns true for now
    );
  };

  const handleDateChange = async (_: any, selectedDate?: Date) => {
    setShowPicker(false);
    if (selectedDate) {
      const formatted = formatDate(selectedDate);
      setDob(formatted);

      // Call background verification
      const isValid = await verifyDOBWithID(formatted);
      if (isValid) {
        setShowConfirmModal(true);
      }
    }
  };

  const handleManualInput = async (rawText: string) => {
    const formatted = formatDobInput(rawText);
    setDob(formatted);
    try { setSenderDOBString?.(formatted); } catch {}
    // Check if valid mm/dd/yyyy format
    const regex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/(19|20)\d{2}$/;
    if (regex.test(formatted)) {
      const parsed = parseMMDDYYYY(formatted);
      try { setSenderDOB?.(parsed); } catch {}
      const isValid = await verifyDOBWithID(formatted);
      if (isValid) {
        setShowConfirmModal(true);
      }
    }
  };

  // Format partial input as MM/DD/YYYY while typing
  const formatDobInput = (raw: string): string => {
    const digits = (raw || "").replace(/\D/g, "").slice(0, 8);
    const mm = digits.slice(0, 2);
    const dd = digits.slice(2, 4);
    const yyyy = digits.slice(4, 8);
    if (digits.length <= 2) return mm;
    if (digits.length <= 4) return `${mm}/${dd}`;
    return `${mm}/${dd}/${yyyy}`;
  };

  const formatDate = (date: Date) => {
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  const parseMMDDYYYY = (text: string): Date => {
    const [mm, dd, yyyy] = text.split("/").map((p) => parseInt(p, 10));
    return new Date(yyyy, mm - 1, dd);
  };

  return (
    <View style={styles.container}>
      <Text style={styles.title}>{t("SENDER_DOB_TITLE")}</Text>
      <Text style={styles.subtitle}>{t("SENDER_DOB_SUBTITLE")}</Text>

      {/* DOB Input with calendar icon */}
      <View style={styles.inputWrapper}>
        <TextInput
          autoFocus
          style={styles.input}
          placeholder="MM/DD/YYYY"
          keyboardType="numeric"
          value={dob}
          onChangeText={handleManualInput}
        />
        <TouchableOpacity onPress={() => setShowPicker(true)}>
            <Image
          source={IdCard}
          style={{height: 24, width: 24}}        
        />
        </TouchableOpacity>
      </View>

      {/* Native Date Picker */}
      {/* {showPicker && (
        <DateTimePicker
          value={new Date(1990, 0, 1)}
          mode="date"
          display={Platform.OS === "ios" ? "spinner" : "default"}
          onChange={handleDateChange}
          maximumDate={new Date()}
        />
      )} */}


      {/* Bottom Popup Confirmation */}
      <View style={styles.infoBox}>
        <Text style={styles.infoText}>
          {t("YOUR_INFO_IS_ALWAYS_KEPT_PRIVATE")}
        </Text>
      </View>
      <Modal
        visible={showConfirmModal}
        animationType="slide"
        transparent={true}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.bottomSheet}>
            <Text style={styles.modalTitle}>{t("SENDER_DOB_CONFIRMATION_TITLE")}</Text>
            <Text style={styles.modalText}>
              {t("SENDER_DOB_CONFIRM_SUBTITLE")}
            </Text>
            <Text style={styles.modalDOB}>{dob}</Text>

            <TouchableOpacity
              style={styles.confirmBtn}
              onPress={() => setShowConfirmModal(false)}
            >
              <Text style={styles.confirmText}>{t("SENDER_DOB_CONFIRM")}</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.cancelBtn}
              onPress={() => setShowConfirmModal(false)}
            >
              <Text style={styles.cancelText}>{t("SENDER_DOB_CANCEL")}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 24,
    marginTop: 16,
    backgroundColor: "#fff"
  },
  title: { fontSize: 22, fontWeight: "700", marginBottom: 4 },
  subtitle: { fontSize: 16, color: "#555", marginBottom: 16 },
  inputWrapper: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#f5f5f5",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  input: { flex: 1, fontSize: 16 },
  modalOverlay: {
    flex: 1,
    justifyContent: "flex-end",
    backgroundColor: "rgba(0,0,0,0.4)",
  },
  bottomSheet: {
    backgroundColor: "#fff",
    padding: 20,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  modalTitle: { fontSize: 18, fontWeight: "700", marginBottom: 10 },
  modalText: { fontSize: 15, color: "#555" },
  modalDOB: {
    fontSize: 18,
    fontWeight: "600",
    marginTop: 10,
    marginBottom: 20,
    textAlign: "center",
  },
  confirmBtn: {
    backgroundColor: "#000",
    padding: 14,
    borderRadius: 8,
    alignItems: "center",
  },
  confirmText: { color: "#fff", fontWeight: "600", fontSize: 16 },
  cancelBtn: { marginTop: 10, alignItems: "center" },
  cancelText: { fontSize: 15, color: "#666" },
  infoBox: {
    marginTop: 20,
    padding: 12,
    borderWidth: 1,
    borderColor: "#FFF",
    borderRadius: 8,
  },
  infoText: { fontSize: 14, color: "#FFF" },
});

export default BillPayDOBStep;